/**
 * ResmoAuthenticitySeal — unified seal renderer.
 *
 * The visual artwork now comes from a single canonical source: the backend
 * SVG endpoint at `/api/public/share/{token}/badge.svg` (which itself embeds
 * the user-supplied certificate PNG + three dynamic overlay text lines).
 * This component is a thin wrapper that:
 *   1. Enforces the strict 100% honesty gate (all_reviewed && pending===0 && flag_count>=1).
 *   2. Renders the backend SVG via <img> so changes to the artwork only need
 *      to ship in one place.
 *   3. Wraps it in a metallic-gradient gray card to complement the silver
 *      of the seal (used to be cream + orange shadow; repainted Mar 2026).
 *   4. Offers a "Download printable PDF" button that hits
 *      `/api/public/share/{token}/certificate.pdf` for a clean US Letter PDF
 *      candidates can print and pin to a portfolio.
 *
 * Props:
 *   - verified: { share_token, flag_count, pending_count, all_reviewed, summary, audited_at }
 *   - compact:  small inline pill (default false) vs. large seal-style card
 *   - tokenOverride: optional explicit share_token (Welcome page preview uses
 *                    the canonical fixture token so it always renders an
 *                    authentic-looking sample).
 */
import React, { useState } from "react";
import { ShieldCheck, Check, Info, Download } from "lucide-react";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ── Metallic palette matching the silver ring of the certificate ──
// Picked from the seal's silver gradient (#E8ECF1 highlight, #8B939E shadow).
const PALETTE = {
  light: "#E8ECF1",       // lightest highlight
  mid: "#C2C8D0",         // mid-platinum
  dim: "#9CA3AF",         // medium silver
  dark: "#6B7280",        // deeper slate
  shadow: "#374151",      // shadow / outline ink
  ink: "#1F2937",         // body text on metallic
  pillBg: "#475569",      // dark slate for the eyebrow pill
  pillFg: "#F9FAFB",      // off-white text on pillBg
};

// CSS background string reused by wrapper + compact pill so the metallic
// gradient is identical in both contexts.
const METALLIC_BG = `
  linear-gradient(135deg,
    ${PALETTE.light} 0%,
    ${PALETTE.mid} 35%,
    ${PALETTE.dim} 55%,
    ${PALETTE.mid} 75%,
    ${PALETTE.light} 100%
  )
`;

function buildBadgeUrl(token) {
  if (!token) return null;
  return `${BACKEND}/api/public/share/${token}/badge.svg`;
}

function buildPdfUrl(token) {
  if (!token) return null;
  return `${BACKEND}/api/public/share/${token}/certificate.pdf`;
}

export default function VerifiedBadge({ verified, compact = false, tokenOverride = null }) {
  const [open, setOpen] = useState(false);
  if (!verified) return null;

  // STRICT 100% gate — every claim reviewed and at least one actually flagged.
  const fullyReviewed = verified.all_reviewed === true && (verified.pending_count ?? 0) === 0;
  const hasClaims = (verified.flag_count ?? 0) >= 1;
  if (!fullyReviewed || !hasClaims) return null;

  const token = tokenOverride || verified.share_token;
  const badgeUrl = buildBadgeUrl(token);
  const pdfUrl = buildPdfUrl(token);
  if (!badgeUrl) return null;

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono-rm uppercase text-[10px] tracking-widest px-2 py-1 rounded"
        style={{
          background: METALLIC_BG,
          color: PALETTE.ink,
          border: `1px solid ${PALETTE.shadow}`,
          fontWeight: 700,
          // subtle inner highlight to make the metallic surface read as real
          boxShadow: `inset 0 1px 0 0 ${PALETTE.light}, 0 1px 2px 0 rgba(31,41,55,0.15)`,
        }}
        data-testid="verified-badge-compact"
        title={verified.summary || `${verified.flag_count} claims reviewed`}
      >
        <ShieldCheck size={11} /> · VERIFIED · CERTIFIED
      </span>
    );
  }

  const reviewedAt = verified.audited_at ? new Date(verified.audited_at) : null;
  const reviewedLabel = reviewedAt ? reviewedAt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <div
      className="rm-card-static p-4 md:p-5"
      style={{
        background: METALLIC_BG,
        borderColor: PALETTE.shadow,
        // Multi-layer shadow gives the card a brushed-metal physicality
        // (inset highlight + drop shadow in a darker slate).
        boxShadow: `4px 4px 0 0 ${PALETTE.shadow}, inset 0 2px 0 0 ${PALETTE.light}`,
      }}
      data-testid="verified-badge"
    >
      <div className="flex items-center gap-4 flex-wrap">
        {/* Canonical seal artwork — backend SVG embeds the certificate PNG
            (now with transparent background so it floats on this metallic card). */}
        <img
          src={badgeUrl}
          alt="Resmo Seal of Authenticity — 100% reviewed"
          width={160}
          height={160}
          style={{ display: "block", flexShrink: 0 }}
          loading="lazy"
          data-testid="verified-badge-svg"
        />

        <div className="flex-1 min-w-[200px]">
          <span
            className="inline-flex items-center gap-1 font-mono-rm uppercase text-[10px] tracking-widest px-2 py-1 rounded"
            style={{
              background: PALETTE.pillBg,
              color: PALETTE.pillFg,
              border: `2px solid ${PALETTE.shadow}`,
              fontWeight: 700,
              boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.15)`,
            }}
            data-testid="verified-badge-header-pill"
          >
            <ShieldCheck size={11} /> · VERIFIED · CERTIFIED
          </span>
          <h3 className="font-display uppercase text-lg leading-tight mt-2"
              style={{ color: PALETTE.ink, letterSpacing: "-0.01em" }}>
            Every claim was reviewed.
          </h3>
          <p className="text-[13px] leading-snug mt-1" style={{ color: PALETTE.shadow }}>
            <strong style={{ color: PALETTE.ink }}>{verified.flag_count}</strong>{" "}
            {verified.flag_count === 1 ? "claim" : "claims"} traced back to the candidate&apos;s base resume — no fabrications, no embellishments. Resmo audited; the candidate approved.
          </p>
          {reviewedLabel && (
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-1.5"
                 style={{ color: PALETTE.dark, fontWeight: 700 }}>
              · Reviewed {reviewedLabel}
            </div>
          )}

          {/* Action row — "What does this mean?" + Download PDF */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: PALETTE.ink, fontWeight: 700, textDecoration: "underline" }}
              data-testid="verified-badge-toggle"
            >
              {open ? "Hide details" : "What does this mean?"} <Info size={11} />
            </button>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1 rounded"
                style={{
                  background: PALETTE.shadow,
                  color: PALETTE.pillFg,
                  border: `1px solid ${PALETTE.ink}`,
                  fontWeight: 700,
                  boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.12)`,
                }}
                data-testid="verified-badge-download-pdf"
                title="Download a printable PDF of this certificate"
              >
                <Download size={11} /> Download PDF
              </a>
            )}
          </div>

          {open && (
            <div className="rm-card-static p-3 mt-2"
                 style={{ background: "white", borderColor: PALETTE.dim }}
                 data-testid="verified-badge-details">
              <p className="text-[12px] leading-snug" style={{ color: PALETTE.shadow }}>
                Resmo&apos;s Honesty Check flags every claim in a tailored resume that doesn&apos;t trace back to the candidate&apos;s original uploaded resume. The candidate then reviews each flag (approve / revise / remove). A seal only renders when <strong>every</strong> flag is resolved — no partial credit. Click the seal to view the full audit at the share link&apos;s source.
              </p>
              <ul className="mt-2 space-y-1 text-[12px]" style={{ color: PALETTE.ink }}>
                <li className="flex items-start gap-1.5">
                  <Check size={11} style={{ color: "#0E5A4A", marginTop: 3 }} />
                  <span>Verified against the candidate&apos;s base resume.</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <Check size={11} style={{ color: "#0E5A4A", marginTop: 3 }} />
                  <span>{verified.flag_count} {verified.flag_count === 1 ? "claim" : "claims"} reviewed, 0 pending.</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <Check size={11} style={{ color: "#0E5A4A", marginTop: 3 }} />
                  <span>Owner can revoke this seal at any time from /account.</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
